import {groupByUniqueKey} from '@benchling/js-utils';
import produce from 'immer';
import {Node} from 'reactflow';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  EdgeWithData,
  FlowchartRenderData,
  NodeWithData,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  convertNodeForReactFlow,
  findNode,
} from 'processes/graph-builder/flowchart-viz/util/ConvertFlowchartForReactFlowUtil';
import {
  ProcessFlowchartStaticNode,
  ProcessStaticFlowchart,
  ProcessFlowchartNodeWidth,
  ProcessFlowchartNodeHeight,
  ProcessFlowchartStaticEdge,
  PROCESS_FLOWCHART_EDITOR_PADDING,
  ProcessFlowchartEdgeType,
  PROCESS_FLOWCHART_EDGE_LABEL_OPTIONS,
  processMarkerEndOptions,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  getNodeTypeForProcessStaticFlowchart,
  getClientIdForProcessFlowchartEdge,
} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';

import computeDefaultPositionsForFlowchart, {
  EdgeClientIdToLabelDictionary,
} from '../../../graph-builder/flowchart-viz/util/computeDefaultPositionsForFlowchart';

function convertStaticNodeForReactFlow(node: ProcessFlowchartStaticNode): Node {
  const convertedNode = convertNodeForReactFlow(node, node.layout!.position);
  return produce(convertedNode, (draftConvertedNode) => {
    const {data, ...rest} = draftConvertedNode;
    draftConvertedNode.data.layout = {
      ...rest,
      width: node.instanceData.width,
      height: node.instanceData.height,
    };
  });
}

function validateFlowchartIntegrity(flowchart: ProcessStaticFlowchart): void {
  assert(flowchart.nodes.length >= 2, 'Flowchart found with fewer than 2 nodes');
  assert(flowchart.edges.length >= 1, 'Flowchart found with no edges');

  for (const node of flowchart.nodes) {
    const edge = flowchart.edges.find(
      ({fromNodeClientId, toNodeClientId}) =>
        node.clientId === fromNodeClientId || node.clientId === toNodeClientId
    );
    assert(edge != null, `Disconnected node found in flowchart: ${node.clientId}`);
  }

  // Ensure every edge is made up of nodes in this flowchart
  for (const edge of flowchart.edges) {
    assert(
      findNode(flowchart.nodes, edge.fromNodeClientId) != null &&
        findNode(flowchart.nodes, edge.toNodeClientId) != null,
      `Edge found with unknown node id: (${edge.fromNodeClientId} -> ${edge.toNodeClientId})`
    );
  }
}

function attachLayoutToNodes(nodes: Array<ProcessFlowchartStaticNode>): Array<ProcessFlowchartStaticNode> {
  return nodes.map((node) => ({
    ...node,
    layout: {
      ...(node.layout ?? {position: {x: 0, y: 0}}),
    },
  }));
}

function getEdgeLabelDictionary(flowchart: ProcessStaticFlowchart): EdgeClientIdToLabelDictionary {
  const nodeById = groupByUniqueKey(flowchart.nodes, ({clientId}) => clientId);
  const dictionary: EdgeClientIdToLabelDictionary = {};
  for (const edge of flowchart.edges) {
    const clientId = getClientIdForProcessFlowchartEdge(edge.fromNodeClientId, edge.toNodeClientId);
    dictionary[edge.clientId] = nodeById[edge.fromNodeClientId]?.outboundEdgeIdToLabel?.[clientId] || null;
  }

  return dictionary;
}

/**
 * Converts our internal Flowchart structure into something that can be passed directly
 * to ReactFlow
 */
export default function convertStaticProcessFlowchartForReactFlow(
  flowchart: ProcessStaticFlowchart
): FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge> {
  const nodes: Array<NodeWithData<ProcessFlowchartStaticNode>> = [];
  const edges: Array<EdgeWithData<ProcessFlowchartStaticEdge>> = [];

  validateFlowchartIntegrity(flowchart);
  const edgeClientIdToLabelDictionary = getEdgeLabelDictionary(flowchart);
  const rootNode = flowchart.nodes.find(
    (node) => node.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  assert(rootNode != null, 'Root node must exist');
  const arrangedFlowchart = computeDefaultPositionsForFlowchart(
    {
      ...flowchart,
      nodes: attachLayoutToNodes(flowchart.nodes),
    },
    edgeClientIdToLabelDictionary,
    {
      defaultNodeHeight: ProcessFlowchartNodeHeight.DEFAULT,
      defaultNodeWidth: ProcessFlowchartNodeWidth.DEFAULT,
      flowchartEditorPadding: PROCESS_FLOWCHART_EDITOR_PADDING,
    }
  );

  let boundingYTop: number = Infinity;
  let boundingYBottom: number = 0;
  for (const node of arrangedFlowchart.nodes) {
    const position = node.layout!.position;
    nodes.push({
      ...convertStaticNodeForReactFlow(node),
      type: getNodeTypeForProcessStaticFlowchart(node, flowchart.edges),
    });
    boundingYBottom = Math.max(boundingYBottom, position.y);
    boundingYTop = Math.min(boundingYTop, position.y);
  }

  for (const edge of arrangedFlowchart.edges) {
    const clientId = getClientIdForProcessFlowchartEdge(edge.fromNodeClientId, edge.toNodeClientId);
    edges.push({
      id: clientId,
      data: edge,
      type: ProcessFlowchartEdgeType.STATIC_VIEWER,
      source: edge.fromNodeClientId,
      target: edge.toNodeClientId,
      markerEnd: processMarkerEndOptions(),
      style: {strokeWidth: '1.5px'},
      label: edgeClientIdToLabelDictionary[edge.clientId],
      ...PROCESS_FLOWCHART_EDGE_LABEL_OPTIONS,
    });
  }

  return {nodes, edges, centersBoundingBox: {top: boundingYTop, bottom: boundingYBottom}};
}
